import { useState, useEffect, useCallback } from 'react'
import { supabase } from './supabase'
import {
  Order,
  OrderItem,
  OrderItemOption,
  KitchenStation,
  VInventoryDashboard,
  VInventoryAlert,
  InventoryMovement,
  VMenuProfitability,
} from './types'

export interface OrderWithDetails extends Order {
  items: (OrderItem & { options: OrderItemOption[] })[]
}

export interface ActionResult {
  success: boolean
  error?: string
}

export function useAdminOrders(restaurantId: string | undefined) {
  const [orders, setOrders] = useState<OrderWithDetails[]>([])
  const [loading, setLoading] = useState(true)

  const assembleOrders = useCallback(async (ordersData: any[]) => {
    if (!ordersData || ordersData.length === 0) return []
    const orderIds = ordersData.map((o) => o.id)
    const { data: itemsData } = await supabase
      .from('order_items')
      .select('*')
      .in('order_id', orderIds)
    const itemIds = itemsData?.map((i) => i.id) || []
    let optionsData: OrderItemOption[] = []
    if (itemIds.length > 0) {
      const { data: optData } = await supabase
        .from('order_item_options')
        .select('*')
        .in('order_item_id', itemIds)
      optionsData = optData || []
    }
    return ordersData.map((order) => {
      const orderItems = (itemsData || []).filter(
        (item) => item.order_id === order.id,
      )
      return {
        ...order,
        items: orderItems.map((item) => ({
          ...item,
          options: optionsData.filter((opt) => opt.order_item_id === item.id),
        })),
      }
    })
  }, [])

  useEffect(() => {
    if (!restaurantId) return

    async function fetchOrders() {
      try {
        setLoading(true)
        const { data: ordersData, error: ordersError } = await supabase
          .from('orders')
          .select('*')
          .eq('restaurant_id', restaurantId)
          .order('created_at', { ascending: false })
          .limit(50)

        if (ordersError) throw ordersError

        if (!ordersData || ordersData.length === 0) {
          setOrders([])
          return
        }

        const assembled = await assembleOrders(ordersData)
        setOrders(assembled)
      } catch (error) {
        console.error('Error fetching admin orders:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchOrders()

    const interval = setInterval(fetchOrders, 15000)
    return () => clearInterval(interval)
  }, [restaurantId, assembleOrders])

  // ─── Refetch helper ────────────────────────────────────────
  const refetchOrders = useCallback(async () => {
    try {
      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select('*')
        .eq('restaurant_id', restaurantId)
        .order('created_at', { ascending: false })
        .limit(50)
      if (ordersError) throw ordersError
      if (!ordersData || ordersData.length === 0) {
        setOrders([])
        return
      }
      const assembled = await assembleOrders(ordersData)
      setOrders(assembled)
    } catch (err) {
      console.error('[NOIZA] refetchOrders error:', err)
    }
  }, [restaurantId, assembleOrders])

  // ─── Optimistic update helper ──────────────────────────────
  const optimisticUpdateOrder = useCallback(
    (orderId: string, newStatus: string) => {
      setOrders((prev) =>
        prev.map((o) =>
          o.id === orderId ? { ...o, status: newStatus as any } : o,
        ),
      )
    },
    [],
  )

  const optimisticUpdateItem = useCallback(
    (itemId: string, newStatus: string) => {
      setOrders((prev) =>
        prev.map((o) => ({
          ...o,
          items: o.items.map((item) =>
            item.id === itemId ? { ...item, status: newStatus as any } : item,
          ),
        })),
      )
    },
    [],
  )

  // ─── RPC with fallback helper ──────────────────────────────
  const tryRpcWithFallback = useCallback(
    async (
      rpcName: string,
      rpcParams: Record<string, any>,
      fallbackFn: () => Promise<void>,
    ): Promise<ActionResult> => {
      // Try RPC first
      try {
        const { error } = await supabase.rpc(rpcName, rpcParams)
        if (!error) {
          await refetchOrders()
          return { success: true }
        }
        console.warn(
          `[NOIZA] RPC ${rpcName} failed:`,
          error.message,
          '— trying fallback',
        )
      } catch (rpcErr) {
        console.warn(
          `[NOIZA] RPC ${rpcName} exception:`,
          rpcErr,
          '— trying fallback',
        )
      }

      // Fallback to direct DB update
      try {
        await fallbackFn()
        await refetchOrders()
        return { success: true }
      } catch (fallbackErr: any) {
        console.error(
          `[NOIZA] Fallback for ${rpcName} also failed:`,
          fallbackErr,
        )
        await refetchOrders()
        return {
          success: false,
          error: fallbackErr?.message || `Error al ejecutar ${rpcName}`,
        }
      }
    },
    [refetchOrders],
  )

  // ─── Order-level actions with fallback ─────────────────────
  const acceptOrder = useCallback(
    async (orderId: string): Promise<ActionResult> => {
      console.log('[NOIZA] acceptOrder:', orderId)
      optimisticUpdateOrder(orderId, 'accepted')
      return tryRpcWithFallback(
        'accept_order',
        { p_order_id: orderId },
        async () => {
          const { error } = await supabase
            .from('orders')
            .update({
              status: 'accepted',
              updated_at: new Date().toISOString(),
            })
            .eq('id', orderId)
          if (error) throw error
        },
      )
    },
    [tryRpcWithFallback, optimisticUpdateOrder],
  )

  const startOrderPreparation = useCallback(
    async (orderId: string): Promise<ActionResult> => {
      console.log('[NOIZA] startOrderPreparation:', orderId)
      optimisticUpdateOrder(orderId, 'preparing')
      return tryRpcWithFallback(
        'start_order_preparation',
        { p_order_id: orderId },
        async () => {
          // Update order status
          const { error: orderErr } = await supabase
            .from('orders')
            .update({
              status: 'preparing',
              updated_at: new Date().toISOString(),
            })
            .eq('id', orderId)
          if (orderErr) throw orderErr
          // Also move pending items to preparing
          const { error: itemsErr } = await supabase
            .from('order_items')
            .update({ status: 'preparing' })
            .eq('order_id', orderId)
            .eq('status', 'pending')
          if (itemsErr) {
            console.warn(
              '[NOIZA] Could not update items to preparing:',
              itemsErr,
            )
          }
        },
      )
    },
    [tryRpcWithFallback, optimisticUpdateOrder],
  )

  const markOrderReady = useCallback(
    async (orderId: string): Promise<ActionResult> => {
      console.log('[NOIZA] markOrderReady:', orderId)
      optimisticUpdateOrder(orderId, 'ready')
      return tryRpcWithFallback(
        'mark_order_ready',
        { p_order_id: orderId },
        async () => {
          const { error } = await supabase
            .from('orders')
            .update({ status: 'ready', updated_at: new Date().toISOString() })
            .eq('id', orderId)
          if (error) throw error
        },
      )
    },
    [tryRpcWithFallback, optimisticUpdateOrder],
  )

  const deliverOrder = useCallback(
    async (orderId: string): Promise<ActionResult> => {
      console.log('[NOIZA] deliverOrder:', orderId)
      optimisticUpdateOrder(orderId, 'delivered')
      return tryRpcWithFallback(
        'deliver_order',
        { p_order_id: orderId },
        async () => {
          const { error } = await supabase
            .from('orders')
            .update({
              status: 'delivered',
              updated_at: new Date().toISOString(),
            })
            .eq('id', orderId)
          if (error) throw error
        },
      )
    },
    [tryRpcWithFallback, optimisticUpdateOrder],
  )

  const cancelOrder = useCallback(
    async (orderId: string): Promise<ActionResult> => {
      console.log('[NOIZA] cancelOrder:', orderId)
      optimisticUpdateOrder(orderId, 'cancelled')
      return tryRpcWithFallback(
        'cancel_order',
        { p_order_id: orderId },
        async () => {
          const { error } = await supabase
            .from('orders')
            .update({
              status: 'cancelled',
              updated_at: new Date().toISOString(),
            })
            .eq('id', orderId)
          if (error) throw error
        },
      )
    },
    [tryRpcWithFallback, optimisticUpdateOrder],
  )

  // ─── Item-level actions with fallback ──────────────────────
  const markItemPreparing = useCallback(
    async (itemId: string): Promise<ActionResult> => {
      console.log('[NOIZA] markItemPreparing:', itemId)
      optimisticUpdateItem(itemId, 'preparing')
      return tryRpcWithFallback(
        'mark_order_item_preparing',
        { p_order_item_id: itemId },
        async () => {
          const { error } = await supabase
            .from('order_items')
            .update({ status: 'preparing' })
            .eq('id', itemId)
          if (error) throw error
        },
      )
    },
    [tryRpcWithFallback, optimisticUpdateItem],
  )

  const markItemReady = useCallback(
    async (itemId: string): Promise<ActionResult> => {
      console.log('[NOIZA] markItemReady:', itemId)
      optimisticUpdateItem(itemId, 'ready')

      const result = await tryRpcWithFallback(
        'mark_order_item_ready',
        { p_order_item_id: itemId },
        async () => {
          const { error } = await supabase
            .from('order_items')
            .update({ status: 'ready' })
            .eq('id', itemId)
          if (error) throw error
        },
      )

      // Try to sync order status from items
      if (result.success) {
        const parentOrder = orders.find((o) =>
          o.items.some((i) => i.id === itemId),
        )
        if (parentOrder) {
          try {
            await supabase.rpc('sync_order_status_from_items', {
              p_order_id: parentOrder.id,
            })
          } catch (syncErr) {
            // If sync RPC doesn't exist, check manually
            try {
              const { data: freshItems } = await supabase
                .from('order_items')
                .select('status')
                .eq('order_id', parentOrder.id)
              if (freshItems) {
                const allReady = freshItems.every(
                  (i) => i.status === 'ready' || i.status === 'served',
                )
                if (allReady && parentOrder.status === 'preparing') {
                  await supabase
                    .from('orders')
                    .update({
                      status: 'ready',
                      updated_at: new Date().toISOString(),
                    })
                    .eq('id', parentOrder.id)
                }
              }
            } catch (manualSyncErr) {
              console.warn('[NOIZA] Manual sync failed:', manualSyncErr)
            }
          }
          await refetchOrders()
        }
      }

      return result
    },
    [tryRpcWithFallback, optimisticUpdateItem, orders, refetchOrders],
  )

  // Legacy — kept for backward compatibility
  const updateOrderStatus = useCallback(
    async (orderId: string, status: string) => {
      try {
        const { error } = await supabase
          .from('orders')
          .update({ status })
          .eq('id', orderId)
        if (error) throw error
        await refetchOrders()
      } catch (error) {
        console.error('Error updating order status:', error)
      }
    },
    [refetchOrders],
  )

  const updateItemStatus = useCallback(
    async (itemId: string, status: string, orderId: string) => {
      try {
        const { error } = await supabase
          .from('order_items')
          .update({ status })
          .eq('id', itemId)
        if (error) throw error
        await refetchOrders()
      } catch (error) {
        console.error('[NOIZA] Error updating item status:', error)
      }
    },
    [refetchOrders],
  )

  return {
    orders,
    loading,
    updateOrderStatus,
    updateItemStatus,
    acceptOrder,
    startOrderPreparation,
    markOrderReady,
    deliverOrder,
    cancelOrder,
    markItemPreparing,
    markItemReady,
    refetchOrders,
  }
}

export function useKitchenStations(restaurantId: string | undefined) {
  const [stations, setStations] = useState<KitchenStation[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!restaurantId) return

    async function fetchStations() {
      try {
        setLoading(true)
        const { data, error } = await supabase
          .from('kitchen_stations')
          .select('*')
          .eq('restaurant_id', restaurantId)
          .eq('is_active', true)
          .order('sort_order')

        if (error) throw error
        setStations(data || [])
      } catch (error) {
        console.error('Error fetching stations:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchStations()
  }, [restaurantId])

  return { stations, loading }
}

export function useInventoryData(restaurantId: string | undefined) {
  const [inventory, setInventory] = useState<VInventoryDashboard[]>([])
  const [alerts, setAlerts] = useState<VInventoryAlert[]>([])
  const [movements, setMovements] = useState<InventoryMovement[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!restaurantId) return

    async function fetchData() {
      try {
        setLoading(true)
        const [invRes, alertsRes] = await Promise.all([
          supabase
            .from('v_inventory_dashboard')
            .select('*')
            .eq('restaurant_id', restaurantId),
          supabase
            .from('v_inventory_alerts')
            .select('*')
            .eq('restaurant_id', restaurantId),
        ])

        if (invRes.data) setInventory(invRes.data)
        if (alertsRes.data) setAlerts(alertsRes.data)

        // Fetch recent movements
        if (invRes.data && invRes.data.length > 0) {
          const itemIds = invRes.data.map((i) => i.inventory_item_id)
          const { data: movData } = await supabase
            .from('inventory_movements')
            .select('*')
            .eq('restaurant_id', restaurantId)
            .in('inventory_item_id', itemIds)
            .order('created_at', { ascending: false })
            .limit(20)

          if (movData) setMovements(movData)
        }
      } catch (error) {
        console.error('Error fetching inventory:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [restaurantId])

  return { inventory, alerts, movements, loading }
}

export function useMetricsData(restaurantId: string | undefined) {
  const [profitability, setProfitability] = useState<VMenuProfitability[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!restaurantId) return

    async function fetchMetrics() {
      try {
        setLoading(true)
        const { data, error } = await supabase
          .from('v_menu_profitability')
          .select('*')
          .eq('restaurant_id', restaurantId)
          .order('margin_percent', { ascending: false })

        if (error) throw error
        setProfitability(data || [])
      } catch (error) {
        console.error('Error fetching metrics:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchMetrics()
  }, [restaurantId])

  return { profitability, loading }
}
